import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { dataLayerApi } from '@/api/dataLayerApi'
import { setDataLayerServerDefault } from '@/lib/dataLayerBase'
import type { Hl7SourceList } from '@/lib/dataLayer/types'

/**
 * The server-side source allowlist, and the side effect that makes the rest of
 * the tab work: pushing the server's default into the source store so
 * `dataLayerSourceId()` resolves for every plain-module caller.
 *
 * Key is deliberately NOT under `['data-layer']`: that prefix is removed
 * wholesale on a source switch (the tail cursors are per-source), and this
 * list does not vary by source, so clearing it would only flicker the chrome.
 *
 * No poll. The allowlist changes on deploys, not at runtime; a mount-time
 * fetch with the default staleTime is right.
 */
export const dataLayerSourcesKey = ['data-layer-sources'] as const

export function useDataLayerSources() {
  const query = useQuery<Hl7SourceList>({
    queryKey: dataLayerSourcesKey,
    queryFn: dataLayerApi.listSources,
  })

  const data = query.data
  useEffect(() => {
    if (data !== undefined) {
      setDataLayerServerDefault(
        data.defaultSourceId,
        data.sources.map((source) => source.id),
      )
    }
  }, [data])

  return query
}
